import {
	EMode,
	EPages,
	ETopBannerZoneName,
	Footer,
	Header,
	initialDataProvider,
	TSeoKeywordData
} from '@microfrontends/app-shell-v2'
import { ELanguage, HogProvider, useIdentify } from '@microfrontends/react-components'
import React, { useEffect, useState } from 'react'
import { UPDATE_USER } from '../../components/constants'
import FooterScripts from '../../components/footerScripts'
import Head from '../../components/head'
import NotificationComponent from '../../components/Notification'
import { TUser } from '../../components/Notification/type'
import AppContext from '../../context/AppContext'
import translation from '../../helpers/translation'
import { getUserInfoServer } from '../../services/apiClient'
import { IUser } from '../../services/auth/@type'
import { decrypt } from '../../services/auth/cryptor'
import IPageProps, { ETypeNumerologyPage } from '../../services/models'

const msBaseUrl = process.env.NEXT_PUBLIC_MICROSERVICE_DOMAIN ?? ''
const isOptOutUserAgentFilter = process.env.NEXT_PUBLIC_POSTHOG_OPT_OUT_USERAGENT_FILTER === '1'

export enum EDevice {
	MOBILE = 'mobile',
	TABLET = 'tablet',
	LAPTOP = 'laptop',
	DESKTOP = 'desktop'
}

export interface IBottomText {
	html?: string | undefined
	imageUrl?: string | undefined
	ssr?: boolean | undefined
}

export interface IBottomTextPageProps {
	bottomText?: IBottomText | null
}

interface INewPageProps extends IPageProps, IBottomTextPageProps {
	metaCities?: any
	noIndex?: boolean
	externalResponsiveElement?: React.ReactNode
	externalElement?: React.ReactNode
	responsiveCornerColor?: string
	seoKeywords?: TSeoKeywordData[]
}

export default function MainLayout({
	children,
	hiddenHeader,
	user,
	userIP,
	languageCode,
	pageName,
	numerologyPage,
	metaCities,
	metaKeywords,
	metaTitle,
	metaDescription,
	metaCanonical,
	t,
	data,
	noIndex,
	bottomText = null,
	externalResponsiveElement,
	externalElement,
	responsiveCornerColor,
	seoKeywords
}: INewPageProps) {
	const language = languageCode || ELanguage.VI
	const [userState, setUserState] = useState<IUser | null>(user)
	const [serviceWorker, setServiceWorker] = useState<ServiceWorker | null>(
		null
	)
	const [breakpoint, setBreakpoint] = useState<EDevice>(EDevice.DESKTOP)
	const [utm, setUtm] = useState('')
	useIdentify(userState as any, msBaseUrl)

	const notificationProps = {
		langCode: languageCode,
		firebaseConfig: {
			apiKey: process.env.FIREBASE_API_KEY ?? '',
			authDomain: process.env.FIREBASE_AUTH_DOMAIN ?? '',
			databaseURL: process.env.FIREBASE_DATABASE_URL ?? '',
			refreshJWTUrl: `${process.env.VNW_BASE_URL}/my-career-center/employer-message/?action=renewFirebaseJWT`
		},
		jobSeekerUrl: process.env.VNW_BASE_URL ?? '',
		user: {
			...userState,
			accessToken: decrypt(userState?.accessToken ?? '')
		} as TUser,
		baseMsUrl: process.env.API_DOMAIN ?? ''
	}

	// handle device change
	const handleDeviceChange = () => {
		if (window.innerWidth <= 768) {
			setBreakpoint(EDevice.MOBILE)
		} else if (window.innerWidth > 768 && window.innerWidth <= 1024) {
			setBreakpoint(EDevice.TABLET)
		} else if (window.innerWidth > 1024 && window.innerWidth <= 1440) {
			setBreakpoint(EDevice.LAPTOP)
		} else {
			setBreakpoint(EDevice.DESKTOP)
		}
	}
	//get utm tracking in numerology
	const getUtmByNumerologyPage = () => {
		switch (numerologyPage) {
			case ETypeNumerologyPage.HomeNumerology: {
				setUtm(
					'utm_source_navi=numerology&utm_medium_navi=job-will-love&utm_campaign_navi=home'
				)
				return
			}
			case ETypeNumerologyPage.DetailNumerology: {
				setUtm(
					'utm_source_navi=numerology&utm_medium_navi=job-will-love&utm_campaign_navi=detail'
				)
				return
			}
			case ETypeNumerologyPage.HomeCareerPath: {
				setUtm(
					'utm_source_navi=career-path&utm_medium_navi=job-will-love&utm_campaign_navi=home'
				)
				return
			}
			case ETypeNumerologyPage.HomeNumerology: {
				setUtm(
					'utm_source_navi=career-path&utm_medium_navi=job-will-love&utm_campaign_navi=detail'
				)
				return
			}
			default:
				return ''
		}
	}

	async function getUserInfo() {
		try {
			const authUser = await getUserInfoServer()
			if (authUser) {
				setUserState(authUser)
			}
		} catch (error) {
			console.error(error)
		}
	}

	const updateUserState = (e: Event) => {
		if (!e?.detail?.user) {
			return
		}
		setUserState(e?.detail?.user)
	}

	const initSelectData = async () => {
		await initialDataProvider.initSelectData({
			selectDataKey: ['jobFunction', 'industry', 'level'],
			mode: process.env.APP_SHELL_MODE as any,
			langCode: languageCode
		})
	}

	useEffect(() => {
		if (userState === null) {
			getUserInfo()
		}

		handleDeviceChange()

		if ('serviceWorker' in navigator) {
			if (navigator.serviceWorker.controller) {
				setServiceWorker(navigator.serviceWorker.controller)
			}
		}

		initSelectData()

		window.addEventListener(UPDATE_USER, updateUserState)

		return () => {
			window.removeEventListener(UPDATE_USER, updateUserState)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (numerologyPage) {
			getUtmByNumerologyPage()
		}
	}, [numerologyPage])

	const commonResource = languageCode === ELanguage.VI ? 'common' : 'commonEn'

	return (
		<AppContext.Provider
			value={{
				serviceWorker: serviceWorker,
				user: userState,
				userIP: userIP ?? '',
				languageCode: language,
				breakpoint,
				utm: utm,
				data: data,
				t: t
			}}
		>
			<HogProvider
				posthogConfig={{
					key: process.env.NEXT_PUBLIC_POSTHOG_API_KEY ?? '',
					host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '',
					options: {
						optOutUserAgentFilter: isOptOutUserAgentFilter
					}
				}}
			>
				<Head
					keywords={
						metaKeywords ||
						translation('meta_keywords', commonResource, language)
					}
					title={
						metaTitle ||
						translation('meta_title', commonResource, ELanguage.VI)
					}
					description={
						metaDescription ||
						translation(
							'meta_description',
							commonResource,
							ELanguage.VI
						)
					}
					ogImage={`https://images.vietnamworks.com/images/vnw_empower_growth_logo.png?version=${
						process.env.VNW_BUILD_ID ?? ''
					}`}
					url={metaCanonical ?? ''}
					robot={noIndex ? 'noindex, nofollow' : undefined}
				/>

				{!hiddenHeader ? (
					<Header
						mode={process.env.APP_SHELL_MODE as EMode}
						langCode={language}
						user={userState}
						topBannerZone={ETopBannerZoneName.EMPTY}
						pageName={pageName || EPages.SEO_LANDING_PAGE}
						responsive={{
							type: 'search-page',
							externalBottomTabElement: externalResponsiveElement,
							externalElement,
							cornerColor: responsiveCornerColor
						}}
					/>
				) : (
					<></>
				)}
				{children}

				<NotificationComponent {...notificationProps} />

				<div id='overlay' />
				<Footer
					mode={process.env.APP_SHELL_MODE as EMode}
					langCode={language}
					user={userState as any}
					seoKeywordData={seoKeywords}
					bottomSEOContent={
						bottomText || {
							html: ``,
							ssr: false,
							imageUrl: ''
						}
					}
				/>
				<FooterScripts user={userState} metaCities={metaCities} />
			</HogProvider>
		</AppContext.Provider>
	)
}
